import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { PrinterApi } from "../../api/PrinterApi";
import type { CashDrawerFetchOptions } from "../../generated/dto/CashDrawerFetchOptions";
import type { CashDrawerStaff } from "../../generated/dto/CashDrawerStaff";
import type { CreatePrinterStaff } from "../../generated/dto/CreatePrinterStaff";
import type { DeletePrinterStaff } from "../../generated/dto/DeletePrinterStaff";
import type { PrinterFetchOptions } from "../../generated/dto/PrinterFetchOptions";
import type { PrinterPrintReceiptRequest } from "../../generated/dto/PrinterPrintReceiptRequest";
import type { PrinterRef } from "../../generated/dto/PrinterRef";
import type { PrinterSetCredentialsRequest } from "../../generated/dto/PrinterSetCredentialsRequest";
import type { PrinterStaff } from "../../generated/dto/PrinterStaff";
import type { PrinterStaffSearchParams } from "../../generated/dto/PrinterStaffSearchParams";
import type { PrinterStaffSearchResult } from "../../generated/dto/PrinterStaffSearchResult";
import type { UpdatePrinterStaff } from "../../generated/dto/UpdatePrinterStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class PrinterApiClient implements PrinterApi {
  constructor(private readonly client = useClient()) {}

  async getPrinterStaff(id: string, options?: PrinterFetchOptions): Promise<PrinterStaff> {
    const { data } = await this.client.get<unknown>(`/printer/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PrinterFetchOptions") });
    return deserializeCbiDto<PrinterStaff>(data, "generated.dto.PrinterStaff");
  }

  async createPrinterStaff(source: CreatePrinterStaff): Promise<PrinterStaff> {
    const { data } = await this.client.post<unknown>(`/printer/staff`, serializeCbiDto(source, "generated.dto.CreatePrinterStaff"));
    return deserializeCbiDto<PrinterStaff>(data, "generated.dto.PrinterStaff");
  }

  async updatePrinterStaff(source: UpdatePrinterStaff): Promise<PrinterStaff> {
    const { data } = await this.client.put<unknown>(`/printer/staff/${source.printerMAC}`, serializeCbiDto(source, "generated.dto.UpdatePrinterStaff"));
    return deserializeCbiDto<PrinterStaff>(data, "generated.dto.PrinterStaff");
  }

  async deletePrinterStaff(source: DeletePrinterStaff): Promise<void> {
    await this.client.delete(`/printer/staff/${source.printerMAC}`);
  }

  async searchPrinterStaff(params: PrinterStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<PrinterStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/printer/staff/search`, serializeCbiDto(params, "generated.dto.PrinterStaffSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PrinterStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PrinterStaffSearchResult>>(result.items, "generated.dto.PrinterStaffSearchResult") };
    return result;
  }

  async searchPrinterStaffFull(params: PrinterStaffSearchParams, page: number, perPage: number, options?: PrinterFetchOptions): Promise<PaginationResult<PrinterStaff>> {
    const { data } = await this.client.post<unknown>(`/printer/staff/search/full`, serializeCbiDto(params, "generated.dto.PrinterStaffSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PrinterFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PrinterStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PrinterStaff>>(result.items, "generated.dto.PrinterStaff") };
    return result;
  }

  async getPrinterStaffCashDrawers(id: string, options?: CashDrawerFetchOptions): Promise<Array<CashDrawerStaff>> {
    const { data } = await this.client.get<unknown>(`/printer/staff/${id}/cashdrawers`, { params: serializeCbiDto(options, "generated.dto.CashDrawerFetchOptions") });
    return deserializeCbiDto<Array<CashDrawerStaff>>(data, "generated.dto.CashDrawerStaff");
  }

  async hasPasswordStaff(printer: PrinterRef): Promise<boolean> {
    const { data } = await this.client.post<unknown>(`/printer/staff/haspassword`, serializeCbiDto(printer, "generated.dto.PrinterRef"));
    return data as boolean;
  }

  async onlineStaff(printer: PrinterRef): Promise<boolean> {
    const { data } = await this.client.post<unknown>(`/printer/staff/online`, serializeCbiDto(printer, "generated.dto.PrinterRef"));
    return data as boolean;
  }

  async pendingJobsStaff(printer: PrinterRef): Promise<number> {
    const { data } = await this.client.post<unknown>(`/printer/staff/pendingjobs`, serializeCbiDto(printer, "generated.dto.PrinterRef"));
    return data as number;
  }

  async printReceiptStaff(request: PrinterPrintReceiptRequest): Promise<string> {
    const { data } = await this.client.post<unknown>(`/printer/staff/printreceipt`, serializeCbiDto(request, "generated.dto.PrinterPrintReceiptRequest"));
    return data as string;
  }

  async printTestStaff(printer: PrinterRef): Promise<string> {
    const { data } = await this.client.post<unknown>(`/printer/staff/printtest`, serializeCbiDto(printer, "generated.dto.PrinterRef"));
    return data as string;
  }

  async setCredentialsStaff(request: PrinterSetCredentialsRequest): Promise<void> {
    await this.client.post(`/printer/staff/setcredentials`, serializeCbiDto(request, "generated.dto.PrinterSetCredentialsRequest"));
  }
}

export const usePrinterApi = (): PrinterApi => new PrinterApiClient();
